import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Bot, Play, Pause, AlertTriangle, TrendingUp, TrendingDown, DollarSign, Target, Activity, Clock, CheckCircle, XCircle, Zap, Shield, StopCircle, Settings, RefreshCw, RotateCcw, Rocket, Loader2, ArrowRight } from 'lucide-react';
import { format, isToday, isYesterday } from 'date-fns';
import EquityCurveCard from '@/components/EquityCurveCard';
import { useAuth } from '@/contexts/AuthContext';
import { track } from '@/utils/analytics';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { SignalSourcePanel } from '@/components/SignalSourcePanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Render a trade's open time as a calm, human-readable label.
 *   Today · 16:04   |   Yesterday · 09:21   |   May 11 · 16:04
 * Falls back to the raw value if parsing fails so we never crash the row.
 */
function formatOpenedAt(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    const time = format(d, 'HH:mm');
    if (isToday(d)) return `Today · ${time}`;
    if (isYesterday(d)) return `Yesterday · ${time}`;
    return `${format(d, 'MMM d')} · ${time}`;
  } catch {
    return '—';
  }
}

export default function PaperTrading() {
  const { token, user } = useAuth();
  const [settings, setSettings] = useState({
    enabled: false,
    max_trades_per_day: 5,
    min_score_threshold: 70,
    asset_thresholds: { NAS100: 70, EURUSD: 70, GBPUSD: 70 },
    emergency_stop: false,
    trades_today: 0,
    last_reset: null
  });
  const [openTrades, setOpenTrades] = useState([]);
  const [closedTrades, setClosedTrades] = useState([]);
  const [stats, setStats] = useState(null);
  const [riskState, setRiskState] = useState(null);
  const [livePrices, setLivePrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [demoTradeLoading, setDemoTradeLoading] = useState(false);
  const [automationJustEnabled, setAutomationJustEnabled] = useState(false);
  const [firstNudgeDismissed, setFirstNudgeDismissed] = useState(() => {
    try {
      return typeof window !== 'undefined' && sessionStorage.getItem('tp360_first_nudge_dismissed') === '1';
    } catch {
      return false;
    }
  });

  // Sample trade tracking — detect when a user-triggered demo closes so we can show
  // a "this is the aha moment" toast that bridges demo → real product value.
  const sampleClosedShown = useRef(new Set());
  const isSampleTrade = (trade) => typeof trade?.signal_id === 'string' && trade.signal_id.startsWith('demo-');
  const openSampleTrade = openTrades.find(isSampleTrade);

  const flashAutomationEnabled = () => {
    setAutomationJustEnabled(true);
    setTimeout(() => setAutomationJustEnabled(false), 3200);
  };

  // Pip distance for forex pairs only — produces "+18 pips" / "−12 pips".
  // For non-forex (NAS100, BTCUSD…) the multiplier doesn't apply, so we hide pips.
  const FOREX_PIP_PAIRS = ['EURUSD', 'GBPUSD', 'AUDUSD', 'USDCAD', 'USDCHF', 'NZDUSD'];
  const pipDistance = (trade, target) => {
    if (!trade || !FOREX_PIP_PAIRS.includes(trade.symbol)) return null;
    const cur = trade.current_price ?? trade.entry_price;
    if (typeof cur !== 'number' || typeof target !== 'number') return null;
    const diff = (target - cur) * 10000;
    const pips = Math.round(diff);
    if (pips === 0) return '0 pips';
    return `${pips > 0 ? '+' : '−'}${Math.abs(pips)} pips`;
  };

  const handleRunFirstTrade = async () => {
    track('sample_clicked', { surface: 'paper_trading_zero_state' });
    try {
      sessionStorage.setItem('tp360_first_nudge_dismissed', '1');
    } catch {
      /* ignore quota / private-mode errors */
    }
    setFirstNudgeDismissed(true);
    setDemoTradeLoading(true);
    try {
      // Bias the sample toward a fast "aha" close (~30–90s). Tight 5-pip SL / 10-pip TP
      // means natural EURUSD volatility usually trips it within seconds; if the market
      // is still, the timer below pushes the price to TP after 45s so the user always
      // sees the close. Production trades use realistic ranges (this only applies to demo-).
      const eur = livePrices?.EURUSD?.price;
      const entry = typeof eur === 'number' && eur > 0.5 ? eur : 1.0850;
      const sl = +(entry - 0.0005).toFixed(5); // 5 pips
      const tp = +(entry + 0.0010).toFixed(5); // 10 pips, 2R

      const res = await fetch(`${API_URL}/api/paper-trading/trades`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          signal_id: `demo-${Date.now()}`,
          symbol: 'EURUSD',
          signal_type: 'BUY',
          timeframe: '1h',
          entry_price: entry,
          stop_loss: sl,
          take_profit: tp,
          automation_score: 85,
          position_size: 1.0,
          risk_amount: (settings.account_balance ?? 1000) * ((settings.risk_percent ?? 1) / 100),
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to open sample trade');
      }

      const created = await res.json();
      track('sample_opened', {
        trade_id: created?.id,
        symbol: 'EURUSD',
        entry_price: entry,
      });
      toast.success('Sample trade opened by you — this is how automated trades will look and behave.');
      await loadData();

      // Time-to-aha guarantee: if the trade is still open at t+45s, programmatically
      // nudge price to TP so the user always experiences the close moment.
      // If the natural monitor has already closed it, the nudge is harmless.
      setTimeout(async () => {
        try {
          await fetch(
            `${API_URL}/api/paper-trading/trades/${created.id}/update-price?current_price=${tp}`,
            { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
          );
          loadData();
        } catch {
          /* silent — natural monitor will eventually resolve it */
        }
      }, 45000);
    } catch (error) {
      console.error('Run first trade error:', error);
      toast.error(error.message || 'Could not open sample trade — try again in a moment.');
    } finally {
      setDemoTradeLoading(false);
    }
  };

  const handleResetTestAccount = async () => {
    setResetting(true);
    try {
      const res = await fetch(`${API_URL}/api/paper-trading/reset-balance`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        // New full-sandbox-reset response shape: { status, trades_deleted, settings, stats }
        const newBalance = data?.settings?.account_balance ?? data?.account_balance ?? 1000;
        const wiped = data?.trades_deleted ?? 0;

        // Instantly clear local state so the dashboard reflects a clean
        // sandbox before the network refresh lands (no flicker of stale
        // trades / banners on mobile).
        setOpenTrades([]);
        setClosedTrades([]);
        setStats(null);
        setRiskState(null);
        sampleClosedShown.current = new Set();

        toast.success(
          `Sandbox reset to $${Number(newBalance).toFixed(2)}`,
          {
            description:
              wiped > 0
                ? `${wiped} trade${wiped === 1 ? '' : 's'} cleared · stats and risk engine reset.`
                : 'Stats and risk engine reset.',
          }
        );
        await loadData();
      } else {
        toast.error(data.detail || 'Failed to reset test account');
      }
    } catch {
      toast.error('Failed to reset test account');
    } finally {
      setResetting(false);
    }
  };
  const [tempMaxTrades, setTempMaxTrades] = useState(5);
  const [tempMinScore, setTempMinScore] = useState(70);
  const [tempAssetThresholds, setTempAssetThresholds] = useState({ NAS100: 70, EURUSD: 70, GBPUSD: 70 });
  const [confirmEmergencyStop, setConfirmEmergencyStop] = useState(false);
  
  const loadData = useCallback(async () => {
    if (!token) return;
    const authHeader = { Authorization: `Bearer ${token}` };
    try {
      const [settingsRes, openRes, closedRes, statsRes, pricesRes, riskRes] = await Promise.all([
        fetch(`${API_URL}/api/paper-trading/settings`, { headers: authHeader }),
        fetch(`${API_URL}/api/paper-trading/trades?status=open`, { headers: authHeader }),
        fetch(`${API_URL}/api/paper-trading/trades`, { headers: authHeader }),
        fetch(`${API_URL}/api/paper-trading/stats`, { headers: authHeader }),
        fetch(`${API_URL}/api/paper-trading/live-prices`),
        fetch(`${API_URL}/api/paper-trading/risk-state`, { headers: authHeader }),
      ]);
      
      if (settingsRes.ok) {
        const settingsData = await settingsRes.json();
        setSettings(settingsData);
        setTempMaxTrades(settingsData.max_trades_per_day);
        setTempMinScore(settingsData.min_score_threshold || 70);
        setTempAssetThresholds(settingsData.asset_thresholds || { NAS100: 70, EURUSD: 70, GBPUSD: 70 });
      }
      
      if (openRes.ok) {
        const openData = await openRes.json();
        setOpenTrades(openData.filter(t => t.status === 'open'));
      }
      
      if (closedRes.ok) {
        const closedData = await closedRes.json();
        setClosedTrades(closedData.filter(t => t.status !== 'open').slice(0, 20));
      }
      
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }

      if (pricesRes.ok) {
        const p = await pricesRes.json();
        setLivePrices(p.prices || {});
      }

      if (riskRes.ok) {
        setRiskState(await riskRes.json());
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load paper trading data');
    } finally {
      setLoading(false);
    }
  }, [token]);
  
  useEffect(() => {
    loadData();
    
    // Auto-refresh every 10 seconds
    const interval = setInterval(loadData, 10000);
    return () => clearInterval(interval);
  }, [loadData]);

  // Detect when a user-triggered SAMPLE trade has just closed and surface the
  // "this is the aha moment" toast bridging demo → real product value.
  useEffect(() => {
    for (const trade of closedTrades) {
      if (!isSampleTrade(trade)) continue;
      if (sampleClosedShown.current.has(trade.id)) continue;
      sampleClosedShown.current.add(trade.id);

      const hitTp = trade.status === 'closed_tp' || trade.close_reason === 'take_profit';
      const hitSl = trade.status === 'closed_sl' || trade.close_reason === 'stop_loss';
      const where = hitTp ? 'TP' : hitSl ? 'SL' : 'manual close';
      const message = `Sample trade closed at ${where}. This is how automated trades complete.`;

      track(hitTp ? 'sample_closed_tp' : hitSl ? 'sample_closed_sl' : 'sample_closed_manual', {
        trade_id: trade.id,
        pnl: trade.pnl ?? 0,
      });

      const automationOff = !settings.enabled || settings.emergency_stop;
      toast.success(message, {
        description: automationOff
          ? 'Want this to happen automatically? Turn on automation.'
          : 'Automation is on — the system will run setups like this for you.',
        duration: 9000,
        action: automationOff
          ? {
              label: 'Turn on automation',
              onClick: () => {
                track('automation_enabled_from_sample', { trade_id: trade.id });
                handleToggleEnabled(true);
                flashAutomationEnabled();
              },
            }
          : undefined,
      });
    }
    // We intentionally only depend on closedTrades; settings is read at fire-time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closedTrades]);
  
  const handleToggleEnabled = async (enabled) => {
    try {
      const response = await fetch(`${API_URL}/api/paper-trading/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          enabled,
          max_trades_per_day: settings.max_trades_per_day,
          emergency_stop: settings.emergency_stop
        })
      });
      
      if (response.ok) {
        setSettings(prev => ({ ...prev, enabled }));
        toast.success(enabled ? 'Paper trading enabled' : 'Paper trading disabled');
      }
    } catch (error) {
      console.error('Error toggling paper trading:', error);
      toast.error('Failed to update settings');
    }
  };
  
  const handleSaveSettings = async () => {
    // Coerce any in-progress string values into bounded numbers before save
    const clamp = (v, lo, hi, fallback) => {
      const n = parseInt(v, 10);
      if (!n || Number.isNaN(n)) return fallback;
      if (n < lo) return lo;
      if (n > hi) return hi;
      return n;
    };
    const safeMaxTrades = clamp(tempMaxTrades, 1, 20, 5);
    const safeMinScore = clamp(tempMinScore, 50, 90, 70);
    const safeAssetThresholds = {
      NAS100: clamp(tempAssetThresholds.NAS100, 50, 90, 70),
      EURUSD: clamp(tempAssetThresholds.EURUSD, 50, 90, 70),
      GBPUSD: clamp(tempAssetThresholds.GBPUSD, 50, 90, 70),
    };

    try {
      const response = await fetch(`${API_URL}/api/paper-trading/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          enabled: settings.enabled,
          max_trades_per_day: safeMaxTrades,
          min_score_threshold: safeMinScore,
          asset_thresholds: safeAssetThresholds,
          emergency_stop: settings.emergency_stop
        })
      });
      
      if (response.ok) {
        setTempMaxTrades(safeMaxTrades);
        setTempMinScore(safeMinScore);
        setTempAssetThresholds(safeAssetThresholds);
        setSettings(prev => ({ 
          ...prev, 
          max_trades_per_day: safeMaxTrades,
          min_score_threshold: safeMinScore,
          asset_thresholds: safeAssetThresholds
        }));
        setSettingsDialogOpen(false);
        toast.success('Settings saved');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    }
  };
  
  const handleEmergencyStop = async () => {
    try {
      const response = await fetch(`${API_URL}/api/paper-trading/emergency-stop`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, enabled: false, emergency_stop: true }));
        setConfirmEmergencyStop(false);
        toast.warning(`Emergency stop activated! ${data.trades_closed} trades closed.`);
        loadData();
      }
    } catch (error) {
      console.error('Error activating emergency stop:', error);
      toast.error('Failed to activate emergency stop');
    }
  };
  
  const handleResetEmergencyStop = async () => {
    try {
      const response = await fetch(`${API_URL}/api/paper-trading/reset-emergency`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        setSettings(prev => ({ ...prev, emergency_stop: false }));
        toast.success('Emergency stop reset');
      }
    } catch (error) {
      console.error('Error resetting emergency stop:', error);
      toast.error('Failed to reset emergency stop');
    }
  };
  
  const handleCloseTrade = async (tradeId) => {
    try {
      const response = await fetch(`${API_URL}/api/paper-trading/trades/${tradeId}/close`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        toast.success('Trade closed');
        loadData();
      }
    } catch (error) {
      console.error('Error closing trade:', error);
      toast.error('Failed to close trade');
    }
  };
  
  const simulatePriceUpdate = async (trade) => {
    // Simulate random price movement for demo purposes
    const volatility = 0.002; // 0.2% movement
    const randomChange = (Math.random() - 0.5) * 2 * volatility;
    const newPrice = trade.entry_price * (1 + randomChange);
    
    try {
      const response = await fetch(`${API_URL}/api/paper-trading/trades/${trade.id}/update-price?current_price=${newPrice}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        loadData();
      }
    } catch (error) {
      console.error('Error updating price:', error);
    }
  };
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return { label: 'OPEN', className: 'bg-primary/20 text-primary border-primary/50' };
      case 'closed_tp':
        return { label: 'TP HIT', className: 'bg-success/20 text-success border-success/50' };
      case 'closed_sl':
        return { label: 'SL HIT', className: 'bg-destructive/20 text-destructive border-destructive/50' };
      case 'closed_manual':
        return { label: 'CLOSED', className: 'bg-warning/20 text-warning border-warning/50' };
      default:
        return { label: status, className: 'bg-muted/20 text-muted-foreground border-muted/50' };
    }
  };

  // Today's realized P&L (computed client-side from closed trades)
  const todayPnl = (() => {
    const today = new Date().toDateString();
    const todayTrades = closedTrades.filter(
      (t) => t.closed_at && new Date(t.closed_at).toDateString() === today
    );
    const sum = todayTrades.reduce((acc, t) => acc + (t.pnl || 0), 0);
    const wins = todayTrades.filter((t) => (t.pnl || 0) > 0).length;
    const losses = todayTrades.filter((t) => (t.pnl || 0) < 0).length;
    const count = todayTrades.length;
    const winRate = count > 0 ? (wins / count) * 100 : 0;
    const startBalance = settings.account_balance != null
      ? settings.account_balance - sum
      : 1000;
    const pctChange = startBalance > 0 ? (sum / startBalance) * 100 : 0;
    return { sum, count, wins, losses, winRate, pctChange };
  })();

  // "Last signal received" — most recent opened_at across all trades (open or closed).
  // Only surfaced when recent (< 4h); otherwise we show a positive "waiting for setups" message
  // instead of a stale "6d ago" that reads as broken.
  const lastSignalRecent = (() => {
    const allOpens = [...openTrades, ...closedTrades]
      .map((t) => (t.opened_at ? new Date(t.opened_at).getTime() : 0))
      .filter(Boolean);
    if (allOpens.length === 0) return null;
    const mostRecent = Math.max(...allOpens);
    const mins = Math.floor((Date.now() - mostRecent) / 60000);
    if (mins > 240) return null; // stale → hide, fallback copy kicks in
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    return `${hrs}h ago`;
  })();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center space-x-3">
            <Bot className="w-10 h-10 text-primary" />
            <div>
              <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-foreground">
                Paper Trading
              </h1>
              <p className="text-muted-foreground font-medium">
                Automated paper trading with automation-ready patterns. No real money at risk.
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2 sm:space-x-3 flex-wrap gap-y-2">
            <Button onClick={loadData} variant="ghost" size="sm" data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>

            {user?.role === 'admin' && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/10 hover:text-yellow-300"
                    data-testid="reset-test-account-btn"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Reset Test Account
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-card border-border/40 max-w-md" data-testid="reset-confirm-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Reset sandbox to a fresh state?</AlertDialogTitle>
                    <AlertDialogDescription asChild>
                      <div className="space-y-3 text-sm text-muted-foreground">
                        <p className="text-foreground">
                          This wipes your paper-trading sandbox and starts you over from scratch.
                        </p>
                        <div>
                          <p className="text-foreground font-medium mb-1">Will be erased:</p>
                          <ul className="list-disc pl-5 space-y-1">
                            <li>all open trades (closed immediately)</li>
                            <li>all closed-trade history</li>
                            <li>stats: win rate, P&L, drawdown, equity curve</li>
                            <li>risk-engine state: loss streak, daily drawdown, market-quality pauses, emergency stop</li>
                          </ul>
                        </div>
                        <div>
                          <p className="text-foreground font-medium mb-1">Will be kept:</p>
                          <ul className="list-disc pl-5 space-y-1">
                            <li>your account &amp; login</li>
                            <li>your automation settings (risk %, max trades/day, score thresholds)</li>
                            <li>your TradingView webhook URL (custom mode keeps working)</li>
                          </ul>
                        </div>
                        <p className="text-xs">
                          Balance will reset to <span className="text-foreground">${(settings.starting_balance ?? 1000).toFixed(2)}</span> and automation will be turned back on. This cannot be undone.
                        </p>
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="reset-cancel-btn">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleResetTestAccount}
                      disabled={resetting}
                      className="bg-yellow-600 hover:bg-yellow-700 text-white"
                      data-testid="reset-confirm-btn"
                    >
                      {resetting ? 'Resetting…' : 'Yes, wipe and reset'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}

            <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="settings-btn">
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border/40">
                <DialogHeader>
                  <DialogTitle>Automation Settings</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="maxTrades">Max Trades Per Day</Label>
                    <Input
                      id="maxTrades"
                      type="number"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      min="1"
                      max="20"
                      value={tempMaxTrades}
                      onChange={(e) => setTempMaxTrades(e.target.value)}
                      onBlur={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!val || val < 1) setTempMaxTrades(1);
                        else if (val > 20) setTempMaxTrades(20);
                        else setTempMaxTrades(val);
                      }}
                      className="bg-secondary/20 border-border/50"
                      data-testid="max-trades-input"
                    />
                    <p className="text-xs text-muted-foreground">Maximum number of auto trades per day</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="minScore">Global Fallback Threshold</Label>
                    <Input
                      id="minScore"
                      type="number"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      min="50"
                      max="90"
                      value={tempMinScore}
                      onChange={(e) => setTempMinScore(e.target.value)}
                      onBlur={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!val || val < 50) setTempMinScore(50);
                        else if (val > 90) setTempMinScore(90);
                        else setTempMinScore(val);
                      }}
                      className="bg-secondary/20 border-border/50"
                      data-testid="min-score-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Used for assets without specific threshold (50-90)
                    </p>
                  </div>
                  
                  <div className="space-y-3 pt-2 border-t border-border/30">
                    <Label className="text-sm font-semibold">Per-Asset Thresholds</Label>
                    <p className="text-xs text-muted-foreground -mt-1">Override global threshold for specific assets</p>
                    {user?.role !== 'admin' ? (
                      <div
                        className="text-xs text-muted-foreground p-3 rounded bg-secondary/30 border border-dashed border-border/40"
                        data-testid="per-asset-thresholds-locked"
                      >
                        Managed automatically for optimal performance.
                      </div>
                    ) : (
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label htmlFor="nas100Threshold" className="text-xs">NAS100</Label>
                        <Input
                          id="nas100Threshold"
                          type="number"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          min="50"
                          max="90"
                          value={tempAssetThresholds.NAS100 ?? ''}
                          onChange={(e) =>
                            setTempAssetThresholds((prev) => ({ ...prev, NAS100: e.target.value }))
                          }
                          onBlur={(e) => {
                            const val = parseInt(e.target.value, 10);
                            setTempAssetThresholds((prev) => ({
                              ...prev,
                              NAS100: !val || val < 50 ? 50 : val > 90 ? 90 : val,
                            }));
                          }}
                          className="bg-secondary/20 border-border/50 h-8 text-sm"
                          data-testid="nas100-threshold-input"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="eurusdThreshold" className="text-xs">EURUSD</Label>
                        <Input
                          id="eurusdThreshold"
                          type="number"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          min="50"
                          max="90"
                          value={tempAssetThresholds.EURUSD ?? ''}
                          onChange={(e) =>
                            setTempAssetThresholds((prev) => ({ ...prev, EURUSD: e.target.value }))
                          }
                          onBlur={(e) => {
                            const val = parseInt(e.target.value, 10);
                            setTempAssetThresholds((prev) => ({
                              ...prev,
                              EURUSD: !val || val < 50 ? 50 : val > 90 ? 90 : val,
                            }));
                          }}
                          className="bg-secondary/20 border-border/50 h-8 text-sm"
                          data-testid="eurusd-threshold-input"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="gbpusdThreshold" className="text-xs">GBPUSD</Label>
                        <Input
                          id="gbpusdThreshold"
                          type="number"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          min="50"
                          max="90"
                          value={tempAssetThresholds.GBPUSD ?? ''}
                          onChange={(e) =>
                            setTempAssetThresholds((prev) => ({ ...prev, GBPUSD: e.target.value }))
                          }
                          onBlur={(e) => {
                            const val = parseInt(e.target.value, 10);
                            setTempAssetThresholds((prev) => ({
                              ...prev,
                              GBPUSD: !val || val < 50 ? 50 : val > 90 ? 90 : val,
                            }));
                          }}
                          className="bg-secondary/20 border-border/50 h-8 text-sm"
                          data-testid="gbpusd-threshold-input"
                        />
                      </div>
                    </div>
                    )}
                    {user?.role === 'admin' && (
                    <div className="text-xs text-muted-foreground p-2 rounded bg-secondary/30">
                      <p className="font-medium mb-1">Score Guide:</p>
                      <p>60-69 = Good Setup (manual only)</p>
                      <p>70-79 = Strong Setup</p>
                      <p>80+ = Very Strong Setup</p>
                    </div>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={handleSaveSettings} data-testid="save-settings-btn">Save</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Hero: Account Balance */}
      <Card
        className="p-5 sm:p-6 mb-6 border-border/40 bg-gradient-to-br from-primary/10 via-card to-card"
        data-testid="hero-account-balance"
      >
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-sm sm:text-base font-semibold text-muted-foreground mr-1">
            Account Balance:
          </span>
          <span className="text-4xl sm:text-5xl font-black font-mono text-primary leading-none">
            ${(settings.account_balance ?? 1000).toFixed(2)}
          </span>
          {stats && (
            <span
              className={`text-base sm:text-lg font-mono font-bold ${
                stats.total_pnl >= 0 ? 'text-success' : 'text-destructive'
              }`}
              data-testid="hero-pnl-inline"
            >
              {stats.total_pnl >= 0 ? '+' : '−'}${Math.abs(stats.total_pnl).toFixed(2)}
              <span className="text-xs sm:text-sm font-semibold text-muted-foreground ml-1.5">
                ({stats.total_pnl >= 0 ? 'Total Profit' : stats.total_pnl < 0 ? 'Total Loss' : 'Breakeven'})
              </span>
            </span>
          )}
        </div>

        {/* Risk-per-trade subtext */}
        <p className="text-xs text-muted-foreground mt-3">
          Risk {(settings.risk_percent ?? 1).toFixed(1)}% ={' '}
          <span className="font-mono text-foreground">
            ${(((settings.account_balance ?? 1000) * (settings.risk_percent ?? 1)) / 100).toFixed(2)}
          </span>{' '}
          per trade
        </p>
      </Card>

      {/* System status reassurance strip */}
      <div
        className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs"
        data-testid="system-status-strip"
      >
        {settings.emergency_stop ? (
          <span className="inline-flex items-center gap-1.5 text-destructive font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-destructive" />
            System halted — reset emergency stop to resume scanning
          </span>
        ) : settings.enabled ? (
          <span className="inline-flex items-center gap-1.5 text-success font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
            {lastSignalRecent
              ? 'System actively scanning markets…'
              : 'Live market scanning — waiting for high-quality setups'}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-muted-foreground font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />
            Automation paused — turn on to start scanning
          </span>
        )}
        {lastSignalRecent && (
          <span className="text-muted-foreground">
            Last signal <span className="text-foreground font-mono">{lastSignalRecent}</span>
          </span>
        )}
      </div>

      {/* Emergency Stop Banner */}
      {settings.emergency_stop && (
        <Card className="p-4 mb-6 bg-destructive/20 border-destructive/50" data-testid="emergency-banner">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="w-6 h-6 text-destructive" />
              <div>
                <p className="font-bold text-destructive">EMERGENCY STOP ACTIVE</p>
                <p className="text-sm text-muted-foreground">All automation has been halted. All open trades have been closed.</p>
              </div>
            </div>
            <Button onClick={handleResetEmergencyStop} variant="outline" size="sm" data-testid="reset-emergency-btn">
              Reset
            </Button>
          </div>
        </Card>
      )}

      {/* Risk Guardrail Banners — priority-ordered. Only shown when a blocker
          is active (daily drawdown > loss streak > market quality > limit). */}
      {riskState && !riskState.allow && riskState.blocker === 'daily_drawdown' && (
        <Card
          className="p-4 mb-6 bg-destructive/15 border-destructive/40"
          data-testid="risk-banner-daily-drawdown"
        >
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-destructive mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-destructive">Daily loss limit reached</p>
              <p className="text-sm text-muted-foreground mt-0.5">
                Today&apos;s P&amp;L {riskState.metrics.daily_pnl_pct.toFixed(2)}% (limit{' '}
                {riskState.limits.daily_drawdown_pct}%). Trading paused until tomorrow (00:00 UTC).
              </p>
            </div>
          </div>
        </Card>
      )}
      {riskState && !riskState.allow && riskState.blocker === 'loss_streak' && (
        <Card
          className="p-4 mb-6 bg-warning/15 border-warning/40"
          data-testid="risk-banner-loss-streak"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 flex-1">
              <AlertTriangle className="w-5 h-5 text-warning mt-0.5 shrink-0" />
              <div>
                <p className="font-bold text-warning">
                  Paused after {riskState.metrics.consecutive_losses} consecutive losses
                </p>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Review conditions before resuming. Manually re-enable automation below when you&apos;re ready.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleToggleEnabled(true)}
              data-testid="risk-banner-reenable-btn"
            >
              Re-enable
            </Button>
          </div>
        </Card>
      )}
      {riskState && !riskState.allow && riskState.blocker === 'market_quality' && (
        <div
          className="mb-6 px-4 py-2.5 rounded border border-muted-foreground/20 bg-muted/30 text-xs text-muted-foreground flex items-center gap-2"
          data-testid="risk-banner-market-quality"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60" />
          Market conditions weak — waiting for higher-quality setups (last {riskState.market.lookback} scores: {riskState.market.last_scores.join(', ')}).
        </div>
      )}
      {riskState && riskState.throttled_for_losses && riskState.blocker !== 'loss_streak' && (
        <div
          className="mb-6 px-4 py-2.5 rounded border border-warning/30 bg-warning/5 text-xs text-warning/90 flex items-center gap-2"
          data-testid="risk-banner-throttled"
        >
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          After {riskState.metrics.consecutive_losses} consecutive losses, daily trade limit throttled to {riskState.effective_max_trades} (half of {settings.max_trades_per_day}).
        </div>
      )}

      {/* Control Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Main Toggle */}
        <Card
          className={`p-6 border-border/40 transition-shadow ${settings.enabled ? 'bg-success/10' : 'bg-card'} ${
            automationJustEnabled ? 'ring-2 ring-success ring-offset-2 ring-offset-background animate-pulse' : ''
          }`}
          data-testid="toggle-card"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              {settings.enabled ? (
                <Play className="w-5 h-5 text-success" />
              ) : (
                <Pause className="w-5 h-5 text-muted-foreground" />
              )}
              <h2 className="text-lg font-bold">Automation Status</h2>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={handleToggleEnabled}
              disabled={settings.emergency_stop}
              data-testid="automation-toggle"
            />
          </div>
          <p className={`text-2xl font-black ${settings.enabled ? 'text-success' : 'text-muted-foreground'}`}>
            {settings.enabled ? 'ACTIVE' : 'INACTIVE'}
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            {settings.trades_today} / {settings.max_trades_per_day} trades today
          </p>
          {automationJustEnabled && (
            <p
              className="mt-3 text-xs font-semibold text-success flex items-center gap-1.5"
              data-testid="automation-just-enabled"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
              Automation enabled — scanning markets…
            </p>
          )}
          {settings.enabled && !automationJustEnabled && (
            <p
              className="mt-3 text-[11px] text-muted-foreground"
              data-testid="paper-mode-hint"
            >
              Running in paper mode · connect a real account anytime
            </p>
          )}

          {/* Signal Source — default "System signals" with an advanced escape hatch */}
          <div className="mt-4 pt-4 border-t border-border/30">
            <SignalSourcePanel
              settings={settings}
              token={token}
              onUpdated={loadData}
            />
          </div>
        </Card>
        
        {/* Trade Limit */}
        <Card className="p-6 border-border/40 bg-card" data-testid="limit-card">
          <div className="flex items-center space-x-2 mb-4">
            <Target className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-bold">Daily Limit</h2>
          </div>
          <div className="flex items-end space-x-2">
            <span className="text-4xl font-black text-primary">{settings.trades_today}</span>
            <span className="text-xl text-muted-foreground">/ {settings.max_trades_per_day}</span>
          </div>
          <div className="text-xs text-muted-foreground mt-2 space-y-1">
            <p>{settings.max_trades_per_day - settings.trades_today} trades remaining</p>
            <p className="font-medium">Score thresholds:</p>
            <p>NAS100: {settings.asset_thresholds?.NAS100 || 70} | EUR: {settings.asset_thresholds?.EURUSD || 72} | GBP: {settings.asset_thresholds?.GBPUSD || 74}</p>
          </div>
        </Card>
        
        {/* Emergency Stop */}
        <Card className="p-6 border-border/40 bg-card" data-testid="emergency-card">
          <div className="flex items-center space-x-2 mb-4">
            <StopCircle className="w-5 h-5 text-destructive" />
            <h2 className="text-lg font-bold">Emergency Stop</h2>
          </div>
          <Dialog open={confirmEmergencyStop} onOpenChange={setConfirmEmergencyStop}>
            <DialogTrigger asChild>
              <Button
                variant="destructive"
                className="w-full"
                disabled={settings.emergency_stop}
                data-testid="emergency-stop-btn"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                {settings.emergency_stop ? 'STOPPED' : 'EMERGENCY STOP'}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border/40">
              <DialogHeader>
                <DialogTitle className="text-destructive">Confirm Emergency Stop</DialogTitle>
              </DialogHeader>
              <p className="text-muted-foreground">
                This will immediately:
              </p>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                <li>Disable all paper trading automation</li>
                <li>Close all open paper trades at current prices</li>
                <li>Prevent any new trades until reset</li>
              </ul>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setConfirmEmergencyStop(false)}>Cancel</Button>
                <Button variant="destructive" onClick={handleEmergencyStop} data-testid="confirm-emergency-btn">
                  Confirm Emergency Stop
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <p className="text-xs text-muted-foreground mt-2">
            Closes all trades and halts automation
          </p>
        </Card>
      </div>
      
      {/* Daily Performance Summary — quick answer to "how did I do today?" */}
      {(() => {
        const hasZeroActivity = openTrades.length === 0 && closedTrades.length === 0;
        if (hasZeroActivity) {
          // Brand-new user — swap the empty state for a guided first action.
          return (
            <>
              {!firstNudgeDismissed && (
                <p
                  className="mb-3 text-sm text-muted-foreground"
                  data-testid="first-trade-nudge"
                >
                  <ArrowRight className="w-3.5 h-3.5 inline-block mr-1.5 -mt-0.5 text-muted-foreground" />
                  Try your first automated trade — takes ~30 seconds
                </p>
              )}
              <Card
                className="p-5 sm:p-6 mb-6 border-primary/30 bg-gradient-to-br from-primary/15 via-card to-card"
                data-testid="first-trade-cta-card"
              >
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center flex-shrink-0">
                    <Rocket className="w-5 h-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-foreground text-base">
                      See how automation works — open a sample trade manually
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      You trigger this one yourself. It mirrors how the system opens, manages
                      and closes a real automated trade — so you can watch the full flow in action.
                    </p>
                  </div>
                </div>
                <div className="sm:ml-auto">
                  <Button
                    onClick={handleRunFirstTrade}
                    disabled={demoTradeLoading || settings.emergency_stop || !!openSampleTrade}
                    className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-10 px-5"
                    data-testid="run-first-trade-btn"
                  >
                    {demoTradeLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Opening…
                      </>
                    ) : openSampleTrade ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sample running…
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Open sample trade manually
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </Card>
            </>
          );
        }
        return (
          <Card
            className={`p-5 sm:p-6 mb-6 border-border/40 ${
              todayPnl.count === 0
                ? 'bg-card'
                : todayPnl.sum > 0
                  ? 'bg-success/5 border-success/30'
                  : todayPnl.sum < 0
                    ? 'bg-destructive/5 border-destructive/30'
                    : 'bg-card'
            }`}
            data-testid="daily-performance-card"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-baseline flex-wrap gap-x-3 gap-y-1">
                <span className="text-xs uppercase tracking-wider font-bold text-muted-foreground">
                  Today
                </span>
                <span
                  className={`text-3xl sm:text-4xl font-black font-mono leading-none ${
                    todayPnl.count === 0
                      ? 'text-foreground'
                      : todayPnl.sum > 0
                        ? 'text-success'
                        : todayPnl.sum < 0
                          ? 'text-destructive'
                          : 'text-foreground'
                  }`}
                  data-testid="daily-pnl-amount"
                >
                  {todayPnl.sum > 0 ? '+' : todayPnl.sum < 0 ? '−' : ''}
                  ${Math.abs(todayPnl.sum).toFixed(2)}
                </span>
                {todayPnl.count > 0 && (
                  <span
                    className={`text-sm font-mono font-semibold ${
                      todayPnl.sum > 0
                        ? 'text-success'
                        : todayPnl.sum < 0
                          ? 'text-destructive'
                          : 'text-muted-foreground'
                    }`}
                  >
                    ({todayPnl.pctChange >= 0 ? '+' : ''}{todayPnl.pctChange.toFixed(2)}%)
                  </span>
                )}
              </div>

              {todayPnl.count === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="daily-empty-state">
                  Waiting for first trade
                </p>
              ) : (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                  <span className="text-muted-foreground">
                    Trades <span className="text-foreground font-mono font-semibold">{todayPnl.count}</span>
                  </span>
                  <span className="text-muted-foreground">
                    Win rate{' '}
                    <span
                      className={`font-mono font-semibold ${
                        todayPnl.winRate >= 50 ? 'text-success' : 'text-destructive'
                      }`}
                    >
                      {todayPnl.winRate.toFixed(0)}%
                    </span>
                  </span>
                  <span className="text-muted-foreground">
                    <span className="text-success font-mono font-semibold">{todayPnl.wins}</span>
                    <span className="mx-1">/</span>
                    <span className="text-destructive font-mono font-semibold">{todayPnl.losses}</span>
                  </span>
                </div>
              )}
            </div>
          </Card>
        );
      })()}

      {/* Equity curve (last 30 closed trades since last balance reset) */}
      <EquityCurveCard refreshKey={stats?.total_trades ?? 0} />

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className="p-4 border-border/40 bg-card" data-testid="total-trades-stat">
            <p className="text-xs text-muted-foreground mb-1">Total Trades</p>
            <p className="text-2xl font-black font-mono">{stats.total_trades}</p>
          </Card>
          <Card className="p-4 border-border/40 bg-card" data-testid="win-rate-stat">
            <p className="text-xs text-muted-foreground mb-1">Win Rate</p>
            <p className="text-2xl font-black font-mono" style={{ color: stats.win_rate >= 50 ? '#10B981' : '#EF4444' }}>
              {stats.win_rate}%
            </p>
          </Card>
          <Card className="p-4 border-border/40 bg-card" data-testid="wins-losses-stat">
            <p className="text-xs text-muted-foreground mb-1">W / L</p>
            <p className="text-2xl font-black font-mono">
              <span className="text-success">{stats.wins}</span>
              <span className="text-muted-foreground mx-1">/</span>
              <span className="text-destructive">{stats.losses}</span>
            </p>
          </Card>
          <Card className={`p-4 border-border/40 ${stats.total_pnl >= 0 ? 'bg-success/10' : 'bg-destructive/10'}`} data-testid="total-pnl-stat">
            <p className="text-xs text-muted-foreground mb-1">Total P&L</p>
            <p className={`text-2xl font-black font-mono ${stats.total_pnl >= 0 ? 'text-success' : 'text-destructive'}`}>
              {stats.total_pnl >= 0 ? '+' : ''}{stats.total_pnl.toFixed(2)}
            </p>
          </Card>
          {/* Max Drawdown — real risk, not just profit */}
          <Card className="p-4 border-border/40 bg-card" data-testid="max-drawdown-stat">
            <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5">
              Max Drawdown
              <span
                className="text-[10px] text-muted-foreground/70"
                title={`Peak balance: $${(stats.peak_balance ?? 0).toFixed(2)}. Current drawdown: ${(stats.current_drawdown_pct ?? 0).toFixed(2)}%`}
              >
                ⓘ
              </span>
            </p>
            <p
              className={`text-2xl font-black font-mono ${
                (stats.max_drawdown_pct ?? 0) >= 10 ? 'text-destructive' : 'text-foreground'
              }`}
            >
              {(stats.max_drawdown_pct ?? 0).toFixed(1)}%
            </p>
            {stats.current_drawdown_pct > 0 && (
              <p className="text-[10px] text-muted-foreground mt-0.5">
                Now: {stats.current_drawdown_pct.toFixed(1)}%
              </p>
            )}
          </Card>
        </div>
      )}
      
      {/* Open Trades */}
      <Card className="p-6 border-border/40 bg-card mb-6" data-testid="open-trades-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center space-x-2">
            <Activity className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-bold">Open Trades ({openTrades.length})</h2>
          </div>
          {Object.keys(livePrices).length > 0 && (
            <div
              className="flex flex-wrap items-center gap-2 text-xs"
              data-testid="live-prices-ribbon"
            >
              <span className="flex items-center gap-1 text-muted-foreground">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                Live
              </span>
              {Object.entries(livePrices).map(([sym, info]) => (
                <span
                  key={sym}
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-secondary/40 border border-border/30"
                  data-testid={`live-price-${sym}`}
                >
                  <span className="font-semibold text-foreground">{sym}</span>
                  <span className="font-mono text-primary">
                    {info.price.toFixed(sym.includes('USD') && !sym.includes('100') ? 4 : 2)}
                  </span>
                </span>
              ))}
            </div>
          )}
        </div>
        
        {openTrades.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-border/40">
                <tr className="text-left">
                  <th className="p-3 font-bold text-sm text-muted-foreground">Opened</th>
                  <th className="p-3 font-bold text-sm text-muted-foreground">Symbol</th>
                  <th className="p-3 font-bold text-sm text-muted-foreground">Type</th>
                  <th className="p-3 font-bold text-sm text-muted-foreground">Entry</th>
                  <th className="p-3 font-bold text-sm text-muted-foreground">SL</th>
                  <th className="p-3 font-bold text-sm text-muted-foreground">TP</th>
                  <th className="p-3 font-bold text-sm text-muted-foreground">Current</th>
                  <th className="p-3 font-bold text-sm text-muted-foreground">P&L</th>
                  <th className="p-3 font-bold text-sm text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {openTrades.map((trade) => {
                  const sample = isSampleTrade(trade);
                  const dir = trade.signal_type?.toLowerCase() === 'buy' ? 'long' : 'short';
                  const tpPips = pipDistance(trade, trade.take_profit);
                  const slPips = pipDistance(trade, trade.stop_loss);
                  // For longs we want "to TP" to read as positive distance up, "to SL" as down.
                  // pipDistance signs from current → target, which already does this for longs.
                  // For shorts the natural reading flips, but the magnitude + label remains correct.
                  return (
                    <React.Fragment key={trade.id}>
                      <tr className="border-b border-border/20 hover:bg-accent/50" data-testid={`open-trade-${trade.id}`}>
                        <td
                          className="p-3 text-xs sm:text-sm whitespace-nowrap text-foreground"
                          title={trade.id}
                          data-testid={`open-trade-opened-${trade.id}`}
                        >
                          {formatOpenedAt(trade.opened_at || trade.created_at)}
                        </td>
                        <td className="p-3 font-bold">
                          <span className="inline-flex items-center gap-2">
                            {trade.symbol}
                            {sample && (
                              <span
                                className="text-[9px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded bg-primary/15 text-primary border border-primary/30"
                                title="Opened manually to demonstrate automation flow"
                                data-testid={`sample-badge-${trade.id}`}
                              >
                                Sample
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="p-3 capitalize text-sm">{trade.signal_type}</td>
                        <td className="p-3 font-mono">{trade.entry_price.toFixed(2)}</td>
                        <td className="p-3 font-mono text-destructive">{trade.stop_loss.toFixed(2)}</td>
                        <td className="p-3 font-mono text-success">{trade.take_profit.toFixed(2)}</td>
                        <td className="p-3 font-mono">{(trade.current_price || trade.entry_price).toFixed(2)}</td>
                        <td className="p-3">
                          <span className={`font-mono font-bold ${(trade.pnl || 0) >= 0 ? 'text-success' : 'text-destructive'}`}>
                            {(trade.pnl || 0) >= 0 ? '+' : ''}{(trade.pnl || 0).toFixed(2)}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex space-x-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => simulatePriceUpdate(trade)}
                              title="Simulate price update"
                            >
                              <RefreshCw className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleCloseTrade(trade.id)}
                              data-testid={`close-trade-${trade.id}`}
                            >
                              Close
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {sample && (
                        <tr className="border-b border-border/20 bg-primary/5" data-testid={`sample-progress-${trade.id}`}>
                          <td colSpan={9} className="px-3 py-2">
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                              <span className="inline-flex items-center gap-1.5 text-primary font-medium">
                                <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                                Monitoring… will close automatically at TP / SL
                              </span>
                              {tpPips && slPips && (
                                <span className="text-muted-foreground">
                                  <span className="text-success font-mono">
                                    {dir === 'long' ? tpPips : slPips} to TP
                                  </span>
                                  <span className="mx-2 text-gray-700">·</span>
                                  <span className="text-destructive font-mono">
                                    {dir === 'long' ? slPips : tpPips} to SL
                                  </span>
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-muted-foreground text-center py-8">No open trades</p>
        )}
      </Card>
      
      {/* Trade History */}
      <Card className="p-6 border-border/40 bg-card" data-testid="trade-history-card">
        <div className="flex items-center space-x-2 mb-4">
          <Clock className="w-5 h-5 text-primary" />
          <h2 className="text-xl font-bold">Recent Trade History</h2>
        </div>
        
        {closedTrades.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-border/40">
                <tr className="text-left">
                  <th className="p-3 font-bold text-sm text-muted-foreground">Time</th>
                  <th className="p-3 font-bold text-sm text-muted-foreground">Symbol</th>
                  <th className="p-3 font-bold text-sm text-muted-foreground">Type</th>
                  <th className="p-3 font-bold text-sm text-muted-foreground">Entry</th>
                  <th className="p-3 font-bold text-sm text-muted-foreground">Exit</th>
                  <th className="p-3 font-bold text-sm text-muted-foreground">P&L</th>
                  <th className="p-3 font-bold text-sm text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {closedTrades.map((trade) => {
                  const badge = getStatusBadge(trade.status);
                  return (
                    <tr key={trade.id} className="border-b border-border/20 hover:bg-accent/50" data-testid={`closed-trade-${trade.id}`}>
                      <td className="p-3 font-mono text-xs">
                        {trade.closed_at ? format(new Date(trade.closed_at), 'MMM dd, HH:mm') : '-'}
                      </td>
                      <td className="p-3 font-bold">
                        <span className="inline-flex items-center gap-2">
                          {trade.symbol}
                          {isSampleTrade(trade) && (
                            <span
                              className="text-[9px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded bg-primary/15 text-primary border border-primary/30"
                              title="Opened manually to demonstrate automation flow"
                              data-testid={`sample-badge-${trade.id}`}
                            >
                              Sample
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="p-3 capitalize text-sm">{trade.signal_type}</td>
                      <td className="p-3 font-mono">{trade.entry_price.toFixed(2)}</td>
                      <td className="p-3 font-mono">{(trade.current_price || trade.entry_price).toFixed(2)}</td>
                      <td className="p-3">
                        <span className={`font-mono font-bold ${(trade.pnl || 0) >= 0 ? 'text-success' : 'text-destructive'}`}>
                          {(trade.pnl || 0) >= 0 ? '+' : ''}{(trade.pnl || 0).toFixed(2)}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded text-xs font-bold border ${badge.className}`}>
                          {badge.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-muted-foreground text-center py-8">No closed trades yet</p>
        )}
      </Card>
      
      {/* Safety Notice */}
      <Card className="p-4 mt-6 border-border/40 bg-primary/5" data-testid="safety-notice">
        <div className="flex items-start space-x-3">
          <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-foreground">Paper Trading Only</p>
            <p className="text-sm text-muted-foreground">
              This is a simulated trading environment. No real money is at risk. 
              Use this to test your automation strategies before connecting to a real broker.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
